import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Loader2, Lock, CheckCircle2, ShieldCheck, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { resetPassword } from "@/lib/api";
import { scorePassword } from "@/lib/passwordStrength";

/**
 * Reset-password page. Opened from the email link with ?token=<jwt>. On submit
 * we hit /api/auth/reset-password, which validates the JWT + updates the hash
 * + issues a fresh session cookie so the user is auto-signed-in after reset.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const strength = useMemo(() => scorePassword(password), [password]);
  const strengthColors = ["bg-destructive", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-600"];
  const handleCapsKey = (e) => {
    if (typeof e.getModifierState === "function") setCapsOn(e.getModifierState("CapsLock"));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/"), 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't reset — try requesting a fresh link.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="grain min-h-screen grid place-items-center p-6 bg-background" data-testid="reset-invalid">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-2xl font-bold">Missing reset token</h1>
          <p className="mt-2 text-sm text-muted-foreground">This page needs to be opened from the reset-password email link.</p>
          <Link to="/forgot-password" className="mt-6 inline-block font-semibold text-accent hover:underline">
            Request a new link →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen grid place-items-center p-6 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-lg"
        data-testid="reset-password-page"
      >
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center h-11 w-11 rounded-2xl bg-accent/15 text-accent">
            <BookOpen className="h-6 w-6" />
          </span>
          <span className="font-serif text-2xl font-bold">Lectura<span className="text-accent">.</span></span>
        </div>

        {done ? (
          <div className="mt-6 text-center" data-testid="reset-success-state">
            <span className="grid place-items-center h-14 w-14 rounded-2xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 mx-auto">
              <CheckCircle2 className="h-7 w-7" />
            </span>
            <h1 className="mt-4 font-serif text-2xl font-bold">Password updated</h1>
            <p className="mt-2 text-sm text-muted-foreground">Signing you in…</p>
          </div>
        ) : (
          <>
            <h1 className="mt-6 font-serif text-2xl font-bold">Choose a new password</h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              This link is one-time use. Pick something strong — at least 8 characters.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="reset-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleCapsKey}
                  onKeyUp={handleCapsKey}
                  onBlur={() => setCapsOn(false)}
                  placeholder="New password"
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-input bg-background pl-11 pr-24 py-3 outline-none focus:ring-2 focus:ring-ring/60"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="reset-password-toggle"
                  title={showPassword ? "Hide password" : "Show password"}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 rounded-lg bg-accent/10 hover:bg-accent/20 text-accent px-2.5 py-1.5 text-xs font-semibold transition"
                >
                  {showPassword ? <><EyeOff className="h-4 w-4" /> Hide</> : <><Eye className="h-4 w-4" /> Show</>}
                </button>
              </div>
              {capsOn && (
                <div
                  data-testid="reset-caps-lock-warning"
                  className="-mt-2 flex items-center gap-2 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-300 px-3 py-2 text-xs font-medium"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>Caps Lock is on.</span>
                </div>
              )}
              {password && (
                <div className="-mt-2 space-y-1.5" data-testid="reset-password-strength">
                  <div className="flex gap-1" aria-hidden>
                    {[0, 1, 2, 3].map((i) => (
                      <span
                        key={i}
                        className={`h-1 flex-1 rounded-full transition ${
                          i < strength.score ? strengthColors[strength.score] : "bg-border"
                        }`}
                      />
                    ))}
                  </div>
                  <p
                    data-testid="reset-password-strength-label"
                    className={`text-xs font-medium ${
                      strength.score <= 1
                        ? "text-destructive"
                        : strength.score === 2
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-emerald-600 dark:text-emerald-400"
                    }`}
                  >
                    {strength.label} — <span className="font-normal text-muted-foreground">{strength.hint}</span>
                  </p>
                </div>
              )}
              <div className="relative">
                <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="reset-confirm-input"
                  type={showConfirm ? "text" : "password"}
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={handleCapsKey}
                  onKeyUp={handleCapsKey}
                  placeholder="Confirm password"
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-input bg-background pl-11 pr-24 py-3 outline-none focus:ring-2 focus:ring-ring/60"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  data-testid="reset-confirm-toggle"
                  title={showConfirm ? "Hide password" : "Show password"}
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1.5 rounded-lg bg-accent/10 hover:bg-accent/20 text-accent px-2.5 py-1.5 text-xs font-semibold transition"
                >
                  {showConfirm ? <><EyeOff className="h-4 w-4" /> Hide</> : <><Eye className="h-4 w-4" /> Show</>}
                </button>
              </div>
              {error && (
                <p data-testid="reset-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">{error}</p>
              )}
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold disabled:opacity-60 hover:opacity-90 transition"
              >
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Resetting…</> : "Reset password"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
